<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { storeToRefs } from 'pinia'
import { ArrowDown } from '@element-plus/icons-vue'
import { useCompteurStore } from '@/store/compteur.store'
import { useBoars } from '@/store/boars.store'
import { useGroupTemplateStore } from '@/store/groupTemplate.store'
import AnimalService from '@/services/AnimalService'
import { StateOfReform } from '@/models/reform'

// Lot9 / E30 — Liste de consultation des verrats liés à une commande réservée de la date
// sélectionnée. La règle d'identification (Nature=Besoin, SourceType=ClientOrderRow,
// IsReserved=true) vit dans compteur.store (`reservedOrderLines`/`reservedBoarIds`) — partagée
// avec la ligne « Réservé » de l'orientation (U05).
// Décision métier 2026-07-06 (v2) : remplace l'ancienne règle basée sur le plan de monte
// (Nature=Baseline), cf. mémoire project_compteur_verrats_reserves.md.
// Design aligné sur Documents/maquette_verrats_reserves.png (icône verrat conservée telle
// quelle, reste du badge/popup refait pour ressembler à la maquette).

interface ReservedBoarRow {
  id: number
  workNumber: string
  name: string
  isReformed: boolean
  /** ProductionStatut + AptitudeConservation (ex. "P1", "Q2"), affiché même pour un verrat réformé
   * (le badge "R" à côté du nom est un ajout, pas un remplacement). */
  statusCode: string
  /** Groupe plan de monte (gabarit "Groupe de départ") de la commande réservée, ex. "G1" — "HG" si
   * son créneau ne matche aucun gabarit configuré, cf. matchGroupTemplateIndex de CompteursView.vue. */
  group: string
  /** Déjà prélevé (nature=ParcPreleve) ET conditionné (nature=Dispo/Stock) ce jour -> nom barré,
   * pas de date affichée (le jour est déjà connu : celui affiché sur l'écran Compteurs). */
  isHandled: boolean
}

/** ProductionStatut (code3, ex. "P"/"Q") + AptitudeConservation (code3, ex. "1"/"2") concaténés. */
function boarStatusCode(boar: { productionStatus?: { code3?: string }; conservationAptitude?: { code3?: string } }): string {
  return `${boar.productionStatus?.code3 ?? ''}${boar.conservationAptitude?.code3 ?? ''}`
}

const compteurStore = useCompteurStore()
const { datas } = storeToRefs(compteurStore)
const boarsStore = useBoars()
const groupTemplateStore = useGroupTemplateStore()
if (groupTemplateStore.datas.length === 0) groupTemplateStore.load()

const reservedBoarIds = computed<number[]>(() => [...compteurStore.reservedBoarIds])

/** "HH:mm"/"HH:mm:ss" -> minutes depuis minuit — réplique CompteursView.vue:timeToMinutes. */
function timeToMinutes(hhmm: string): number {
  const [h, m] = hhmm.split(':').map(Number)
  return (h || 0) * 60 + (m || 0)
}

/** Index (0-based) du gabarit "Groupe de départ" dont la plage contient ce créneau, ou null si
 * aucun gabarit ne matche / n'est configuré — réplique CompteursView.vue:matchGroupTemplateIndex. */
function matchGroupTemplateIndex(creneau: string): number | null {
  const templates = [...groupTemplateStore.datas].sort((a, b) => a.rank - b.rank)
  if (templates.length === 0) return null
  const t = timeToMinutes(creneau)
  for (let i = 0; i < templates.length; i++) {
    const start = timeToMinutes(templates[i].horaireDebut)
    const end = timeToMinutes(templates[i].horaireFin)
    if (t >= start && t < end) return i
  }
  return null
}

/** Créneau (CTime) de la 1ère commande réservée trouvée pour ce verrat, pour en déduire le groupe. */
const reservedBoarCreneauById = computed<Map<number, string>>(() => {
  const m = new Map<number, string>()
  for (const c of compteurStore.reservedOrderLines) {
    if (!m.has(c.boarId as number) && c.creneau) m.set(c.boarId as number, c.creneau)
  }
  return m
})

/** "G1"/"G2"... si le créneau matche un gabarit configuré, sinon "HG" (hors groupe). */
function groupLabelFor(boarId: number): string {
  const creneau = reservedBoarCreneauById.value.get(boarId)
  if (!creneau) return 'HG'
  const idx = matchGroupTemplateIndex(creneau)
  return idx != null ? `G${idx + 1}` : 'HG'
}

/** Verrats prélevés ce jour (nature=ParcPreleve, U08 : "verrat prélevé le jour"). */
const preleveBoarIds = computed<Set<number>>(() => {
  const ids = new Set<number>()
  for (const c of datas.value) if (c.nature === 'ParcPreleve' && c.boarId != null) ids.add(c.boarId)
  return ids
})

/** Verrats déjà conditionnés (nature=Dispo, orienté) ou en stock (nature=Stock, déjà validé). */
const conditionneBoarIds = computed<Set<number>>(() => {
  const ids = new Set<number>()
  for (const c of datas.value) if ((c.nature === 'Dispo' || c.nature === 'Stock') && c.boarId != null) ids.add(c.boarId)
  return ids
})

/** Prélevé ET conditionné -> le verrat réservé est déjà entièrement traité pour ce jour. */
function isHandled(boarId: number): boolean {
  return preleveBoarIds.value.has(boarId) && conditionneBoarIds.value.has(boarId)
}

/** Verrats résolus hors du store actif (réformé, ou muté vers un autre centre) — le store
 * `boars` ne charge par défaut que les présents (isPresent=true), un verrat réservé réformé
 * y serait donc invisible sans ce fallback. Même pattern que production.store.ts. */
const extraBoars = ref<Map<number, { workNumber: string; name: string; isReformed: boolean; statusCode: string }>>(new Map())

async function resolveMissingBoars(ids: number[]): Promise<void> {
  const missing = ids.filter((id) => !boarsStore.getByIdInStore(id) && !extraBoars.value.has(id))
  if (missing.length === 0) return

  // 1er fallback : verrat réformé du centre courant (absent car isPresent=true au chargement initial).
  await boarsStore.loadReformed()

  const stillMissing = missing.filter((id) => !boarsStore.getByIdInStore(id))
  if (stillMissing.length === 0) return

  // 2e fallback : verrat muté vers un autre centre (loadReformed filtre par centre courant).
  const fetched = await Promise.all(stillMissing.map((id) => AnimalService.getBoarById(id)))
  const valid = fetched.filter((b): b is NonNullable<typeof b> => b != null)
  if (valid.length === 0) return

  const hydrated = await boarsStore.hydrateBoars(valid)
  const next = new Map(extraBoars.value)
  for (const boar of hydrated) {
    next.set(boar.id, {
      workNumber: boar.workNumber ?? '',
      name: boar.name ?? '',
      isReformed: boar.reform?.state === StateOfReform.Validate,
      statusCode: boarStatusCode(boar),
    })
  }
  extraBoars.value = next
}

watch(reservedBoarIds, (ids) => { resolveMissingBoars(ids) }, { immediate: true })

const reservedBoars = computed<ReservedBoarRow[]>(() => {
  const rows: ReservedBoarRow[] = []
  for (const id of reservedBoarIds.value) {
    const boar = boarsStore.getByIdInStore(id)
    if (boar) {
      rows.push({
        id,
        workNumber: boar.workNumber ?? '',
        name: boar.name ?? '',
        isReformed: boar.reform?.state === StateOfReform.Validate,
        statusCode: boarStatusCode(boar),
        group: groupLabelFor(id),
        isHandled: isHandled(id),
      })
      continue
    }
    const extra = extraBoars.value.get(id)
    if (extra) rows.push({ id, ...extra, group: groupLabelFor(id), isHandled: isHandled(id) })
  }

  return rows.sort((a, b) => a.name.localeCompare(b.name))
})
</script>

<template>
  <el-popover placement="bottom-start" :width="440" trigger="click" popper-class="reserved-boars-popover">
    <template #reference>
      <button type="button" class="reserved-boars-trigger">
        <span class="reserved-boars-icon-circle">
          <img src="/images/pig-black.svg" class="reserved-boars-icon" alt="" />
        </span>
        <span class="reserved-boars-trigger__label">{{ $t('compteur.reservedBoars.trigger', { count: reservedBoars.length }) }}</span>
        <el-icon class="reserved-boars-trigger__chevron"><ArrowDown /></el-icon>
      </button>
    </template>

    <div class="reserved-boars-panel">
      <div class="reserved-boars-panel__header">
        <div class="reserved-boars-panel__title">{{ $t('compteur.reservedBoars.title') }}</div>
        <span class="reserved-boars-panel__count">{{ reservedBoars.length }}</span>
      </div>

      <el-table
        :data="reservedBoars"
        size="small"
        max-height="320"
        border
        :empty-text="$t('compteur.reservedBoars.emptyText')"
        :header-cell-style="{ background: '#2B48A2', color: '#ffffff' }"
      >
        <el-table-column :label="$t('compteur.reservedBoars.colWorkNumber')" width="110">
          <template #default="{ row }"><strong>{{ row.workNumber }}</strong></template>
        </el-table-column>
        <el-table-column :label="$t('compteur.reservedBoars.colName')">
          <template #default="{ row }">
            <span :class="{ 'name-handled': row.isHandled }">{{ row.name }}</span>
            <span v-if="row.isReformed" class="reform-badge" :title="$t('compteur.reservedBoars.reformed')">{{ $t('compteur.reservedBoars.reformedShort') }}</span>
          </template>
        </el-table-column>
        <el-table-column :label="$t('compteur.reservedBoars.colStatus')" width="70">
          <template #default="{ row }">{{ row.statusCode }}</template>
        </el-table-column>
        <el-table-column :label="$t('compteur.reservedBoars.colGroup')" width="70">
          <template #default="{ row }">{{ row.group }}</template>
        </el-table-column>
      </el-table>
    </div>
  </el-popover>
</template>

<style scoped>
.reserved-boars-trigger {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 32px;
  padding: 0 12px 0 4px;
  background: #2b48a2;
  color: #ffffff;
  border: none;
  border-radius: 16px;
  cursor: pointer;
  font-family: inherit;
}

.reserved-boars-icon-circle {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #ffffff;
  flex-shrink: 0;
}

.reserved-boars-icon {
  width: 16px;
  height: 16px;
}

.reserved-boars-trigger__label {
  font-size: 13px;
  font-weight: 600;
  white-space: nowrap;
}

.reserved-boars-trigger__chevron {
  font-size: 12px;
}

.reserved-boars-panel__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 10px;
}

.reserved-boars-panel__title {
  font-weight: 700;
  font-size: 15px;
  color: #1c1e21;
}

.reserved-boars-panel__count {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 22px;
  height: 22px;
  padding: 0 6px;
  border-radius: 11px;
  background: #2b48a2;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
}

.reform-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-left: 4px;
  border-radius: 50%;
  background: #6b7280;
  color: #ffffff;
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  vertical-align: middle;
}

.name-handled {
  text-decoration: line-through;
  color: #6b7280;
}

/* Table plus lisible : en-têtes et libellés agrandis (le size="small" d'Element Plus est
   trop compact une fois la popup élargie à 440px). */
:deep(.reserved-boars-panel .el-table th .cell) {
  font-size: 13px;
  font-weight: 700;
}

:deep(.reserved-boars-panel .el-table td .cell) {
  font-size: 14px;
}

</style>
